// ─────────────────────────────────────────────────────────────────────────────
// Statutory interest & late-fee rules — income-tax (234A/234B/234C, 201(1A),
// 234E) and GST (CGST s.50 interest, s.47 late fee) — plus the pure compute
// functions the calculator UI runs entirely in the browser.
//
// RESEARCHED / LAST REVIEWED
//   19 August 2026.
//
// SOURCING POSTURE
//   Every rate, period basis, formula, tolerance band, cap and citation in the
//   RULES array below is carried VERBATIM from the research payload compiled on
//   19 August 2026 (per-rule `sourceUrl` names the primary secondary source).
//   Nothing here is recalled from memory or "filled in" — where the research
//   left an endpoint or mechanic ambiguous, the compute functions flag it with
//   a // TODO(confirm): comment and a visible note instead of guessing.
//
//   All seven rules are `confidence: "confirmed"` in the research. The
//   1961-Act → 2025-Act citation pairs (s.234A→423, 234B→424, 234C→425,
//   201(1A)→398, 234E→427) take effect for tax year 2026-27; GST provisions
//   are untouched by the Income-tax Act, 2025.
//
// CONVENTIONS IMPLEMENTED (each traced to the research text)
//   • "Month or part of a month" for 234A/234B: date-to-date anniversary
//     counting from the start of the period, any fraction rounds UP to a full
//     month (Rule 119A).
//   • "Month or part of a month" for 201(1A): CALENDAR months touched by the
//     interval — the research's own example (deducted 21-May, deposited 8-Jun
//     → "spans 2 part-months = 3%") is reproducible only on that basis, which
//     is also how TRACES computes it.
//   • 234C uses FIXED periods (3/3/3/1 months) — no day counting at all.
//   • GST s.50 interest is DAY-wise per Rule 88B: amount × 18% × days ÷ 365.
//   • GST late fee and 234E run per calendar DAY, no month rounding.
//   • Rule 119A base rounding (down to the nearest ₹100) applies to the
//     income-tax interest bases only — never to GST amounts.
//
// WORKED EXAMPLES (hand-computed from the research formulas; the compute
// functions below reproduce these exactly — verified by script before ship):
//
//   1. s.234A — tax on total income ₹5,00,000; TDS/TCS ₹1,50,000; advance tax
//      ₹1,00,000; no reliefs / MAT credit / self-assessment tax. A = 5,00,000
//      − 1,00,000 − 1,50,000 = ₹2,50,000 (already a multiple of ₹100).
//      Due 16-Sep-2025 (the final CBDT-extended date — a further one-day
//      extension was announced 15-Sep-2025), filed 23-Dec-2025 → period
//      17-Sep → 23-Dec = 3 full months + 6 days → 4 months. Interest = 1% × 4 × 2,50,000 = ₹10,000.
//
//   2. s.234C — tax due on returned income ₹1,00,000; cumulative advance tax
//      ₹10,000 by 15-Jun, ₹30,000 by 15-Sep, ₹60,000 by 15-Dec, ₹90,000 by
//      15-Mar.
//        Q1: required 15% = 15,000; 12% tolerance = 12,000 > 10,000 paid →
//            interest applies; shortfall 5,000 → 1% × 3 × 5,000 = ₹150.
//        Q2: required 45,000; 36% tolerance = 36,000 > 30,000 → applies;
//            shortfall 15,000 → 1% × 3 = ₹450.
//        Q3: required 75,000; shortfall 15,000 → ₹450.
//        Q4: required 1,00,000; shortfall 10,000 → 1% × 1 = ₹100.
//      Total = ₹1,150.
//
//   3. s.201(1A) — the research's own example: TDS ₹10,000 deducted 21-May
//      (on the deductible date, so no late-deduction leg), deposit due 7-Jun,
//      deposited 8-Jun. Late deposit runs from the DEDUCTION date: 21-May →
//      8-Jun touches May and June = 2 part-months → 1.5% × 2 × 10,000 = ₹300.
//
//   Extra sanity (also script-verified): GST s.50(1) cash-ledger tax ₹1,00,000
//   due 20-Apr-2026 paid 20-May-2026 → 30 days → 1,00,000 × 18% × 30/365 =
//   ₹1,479.45. GSTR-3B normal return, AATO ≤ ₹1.5 cr, 100 days late → ₹50 ×
//   100 = ₹5,000 → capped at ₹2,000. 234E: TDS in statement ₹4,800, 60 days
//   late → ₹200 × 60 = ₹12,000 → capped at ₹4,800.
// ─────────────────────────────────────────────────────────────────────────────

export const REVIEWED_ON = "2026-08-19";

export type Confidence = "confirmed" | "likely" | "unverified";

export type RuleKey =
  | "234a"
  | "234b"
  | "234c"
  | "gst_50"
  | "gst_late_fee"
  | "tds_201_1a"
  | "tds_234e";

export type InterestRule = {
  key: RuleKey;
  title: string;
  trigger: string;
  rate: string;
  periodBasis: string;
  formula: string;
  exceptions: ReadonlyArray<string>;
  capsAndFloors?: string;
  /** Citation under the law as historically enacted (1961 Act / CGST as enacted). */
  citationOld: string;
  /** The Income-tax Act 2025 equivalent — or the current GST citation set. */
  citationNew: string;
  confidence: Confidence;
  sourceUrl: string;
};

// ── RULES — verbatim from the 19-Aug-2026 research payload ───────────────────

export const RULES: ReadonlyArray<InterestRule> = [
  {
    key: "234a",
    title: "Interest for default in furnishing return of income (s.234A)",
    trigger:
      "Return of income furnished after the due date (s.139(1), incl. any CBDT-extended date), or not furnished at all, AND net tax remains unpaid. If the entire self-assessment tax was paid on or before the due date, no 234A interest even if the return itself is late (net-unpaid-tax basis; CIT v. Prannoy Roy, SC).",
    rate: "1% simple interest per month or part of a month",
    periodBasis:
      "Per month or part of a month — any fraction of a month counts as a FULL month (Rule 119A). Period runs from the day after the due date to the date of actually furnishing the return (or, if never furnished, to the date of completion of best-judgment assessment u/s 144).",
    formula:
      "Interest = 1% × T × A, where T = months (part rounded UP) from due date to filing date, and A = tax on total income MINUS advance tax paid, TDS/TCS, relief u/s 89/90/90A/91, and MAT/AMT credit — with A rounded DOWN to the nearest multiple of ₹100 (Rule 119A) before multiplying.",
    exceptions: [
      "No interest if A (net unpaid tax) is nil or negative — refund cases attract no 234A",
      "Self-assessment tax paid before the due date is excluded from A for the period after its payment",
      "CBDT due-date extensions shift the start date (e.g. FY 2024-25 non-audit returns: 234A ran only after the extended 16-Sep-2025 date (15-Sep was further extended by one day))",
    ],
    citationOld:
      "s.234A, Income-tax Act 1961 (read with Rule 119A, Income-tax Rules 1962)",
    citationNew:
      "s.423, Income-tax Act 2025 (formula I = 1% × A × T; same mechanics, effective tax year 2026-27)",
    confidence: "confirmed",
    sourceUrl:
      "https://cleartax.in/s/interest-imposed-by-income-tax-department-under-section-234a",
  },
  {
    key: "234b",
    title: "Interest for default in payment of advance tax (s.234B)",
    trigger:
      "Assessee liable to pay advance tax (i.e. estimated net tax liability ≥ ₹10,000) who either paid NO advance tax, or whose advance tax paid is LESS THAN 90% of 'assessed tax' (assessed tax is already net of TDS/TCS and reliefs).",
    rate: "1% simple interest per month or part of a month",
    periodBasis:
      "Per month or part of a month (part = full month, Rule 119A). Runs from 1 April of the assessment year until the date of determination of income u/s 143(1) or the date of regular assessment; where self-assessment tax is paid mid-way, interest on that portion stops at the payment date (s.234B(2)).",
    formula:
      "Interest = 1% × months × (Assessed tax − Advance tax paid), where Assessed tax = tax on total income − TDS/TCS − relief u/s 89/90/90A/91 − MAT/AMT credit. Base rounded down to nearest ₹100. If advance tax paid ≥ 90% of assessed tax, no interest at all.",
    exceptions: [
      "Resident senior citizen (60+) with no income under 'profits and gains of business or profession' is not liable to advance tax (s.207(2)) — 234B can never apply",
      "No interest if liability to pay advance tax (net of TDS) is below ₹10,000",
      "Interest recomputed on reassessment/recomputation increases (s.234B(3)) — a calculator for self-filing can ignore this branch",
    ],
    citationOld: "s.234B, Income-tax Act 1961",
    citationNew:
      "s.424, Income-tax Act 2025 (1%/month, same 90%-of-assessed-tax test, effective tax year 2026-27)",
    confidence: "confirmed",
    sourceUrl:
      "https://cleartax.in/s/interest-imposed-by-income-tax-department-under-section-234b",
  },
  {
    key: "234c",
    title: "Interest for deferment of advance-tax instalments (s.234C)",
    trigger:
      "Shortfall against the cumulative instalment schedule computed on 'tax due on returned income': 15% by 15 June, 45% by 15 Sep, 75% by 15 Dec, 100% by 15 Mar.",
    rate: "1% simple interest per month",
    periodBasis:
      "FIXED periods, not actual delay: 3 months' interest on the shortfall of each of the first three instalments; 1 month's interest on the 15-March shortfall. (No day-counting — each missed quarter costs exactly 3% of that quarter's shortfall, the last costs 1%.)",
    formula:
      "For each of Q1–Q3: Interest = 1% × 3 × max(0, cumulative% × tax due on returned income − advance tax + TDS paid up to that date). For Q4 (15 Mar): 1% × 1 × shortfall from 100%. 44AD/44ADA presumptive assessees: single instalment — 100% by 15 March; interest = 1% × 1 month on any shortfall (nothing due for Jun/Sep/Dec). Base amounts rounded down to nearest ₹100 (Rule 119A).",
    exceptions: [
      "Tolerance bands: NO interest for the 15-June instalment if ≥ 12% of tax due is paid by then, and none for 15-Sep if ≥ 36% is paid (applies to first two instalments only)",
      "No 234C if net tax liability after TDS is < ₹10,000 (no advance-tax obligation)",
      "Resident senior citizen with no business/professional income — no advance-tax obligation, no 234C",
      "Income-driven relaxation (first proviso, incl. Finance Act 2021 dividend addition): shortfall attributable to capital gains, casual income u/s 2(24)(ix) (lottery/crossword/races), dividend income (EXCLUDING deemed dividend u/s 2(22)(e)), or income from a NEW business/profession arising for the first time — no interest if the tax on that income is paid in the remaining instalments falling due after it accrues, or by 31 March where none remain",
    ],
    capsAndFloors:
      "12%/36% tolerances for Q1/Q2; nothing for Q3/Q4 — those require the full 75%/100%.",
    citationOld:
      "s.234C, Income-tax Act 1961 (dividend carve-out inserted by Finance Act 2021, AY 2021-22 onward)",
    citationNew:
      "s.425, Income-tax Act 2025 (1%/3-month and 1-month pattern retained, effective tax year 2026-27)",
    confidence: "confirmed",
    sourceUrl:
      "https://cleartax.in/s/interest-imposed-by-income-tax-department-under-section-234c",
  },
  {
    key: "gst_50",
    title:
      "GST interest on delayed payment of tax / wrongly utilised ITC (CGST s.50)",
    trigger:
      "s.50(1): tax (or part) not paid by the return due date. s.50(3): input tax credit wrongly AVAILED and also UTILISED (mere availment without utilisation attracts no interest — CBIC clarification with Notification 09/2022; ITC is 'utilised' when the electronic credit ledger balance falls below the wrongly availed amount).",
    rate:
      "18% per annum for BOTH heads. s.50(1): 18% notified under Notification 13/2017-Central Tax. s.50(3): 18% notified retrospectively from 1-Jul-2017 by Notification 09/2022-Central Tax dated 05-Jul-2022 — the 24% figure survives ONLY as the statutory ceiling in s.50(3) after the Finance Act 2022 substitution; post-FA-2022 there is NO case where 24% is actually charged.",
    periodBasis:
      "PER DAY, not per month — computed day-wise on a per-annum basis under Rule 88B: interest = amount × 18% × days ÷ 365. s.50(1): from the day after the return due date to the date of payment. s.50(3): from the date of utilisation of the wrongly availed ITC to the date of reversal or payment.",
    formula:
      "Net-cash-liability rule (proviso to s.50(1), inserted retrospectively from 1-Jul-2017 by Finance Act 2021 s.112, notified by Notification 16/2021-CT): for a BELATED RETURN, interest applies only on the portion of tax paid by debiting the electronic CASH ledger — the ITC-set-off portion attracts no interest. Interest = (cash-ledger tax) × 18% × days/365. EXCEPTION: the net-cash benefit is lost if the return is filed after proceedings u/s 73/74 (now also 74A) have commenced — interest then runs on the gross liability.",
    exceptions: [
      "No interest on the ITC-settled portion of a late self-assessed return (net-cash rule, retro 1-Jul-2017)",
      "No s.50(3) interest where wrongly availed ITC was never utilised (credit-ledger balance never dipped below it)",
      "Net-cash benefit unavailable when the return follows commencement of s.73/74/74A proceedings",
    ],
    citationOld:
      "CGST Act 2017 s.50 (as originally enacted, 18%/24%); proviso via Finance Act 2021; s.50(3) substituted by Finance Act 2022 retro w.e.f. 1-Jul-2017",
    citationNew:
      "Current law: CGST Act 2017 s.50 read with Rule 88B and Notifications 13/2017, 16/2021, 09/2022-Central Tax. GST is untouched by the Income-tax Act 2025 — no mapping applies.",
    confidence: "confirmed",
    sourceUrl: "https://www.aubsp.com/cgst-act-section-50-explained/",
  },
  {
    key: "gst_late_fee",
    title: "GST late fee for GSTR-3B and GSTR-1 (CGST s.47 + mirrored SGST)",
    trigger:
      "Return/statement furnished after its due date. Applies per return, per tax period, from the day after the due date until the date of filing.",
    rate:
      "Normal return: ₹50 per day total (₹25 CGST + ₹25 SGST). NIL return (no outward supplies / no tax liability): ₹20 per day total (₹10 + ₹10). Same per-day rates for GSTR-3B and GSTR-1. No late fee under the IGST Act.",
    periodBasis: "Per calendar DAY of continuing default (no month rounding).",
    formula:
      "Late fee = min(per-day rate × days late, applicable cap). Compute CGST and SGST halves separately (each is half the totals shown).",
    exceptions: [
      "Caps apply per return, rationalised from the June-2021 tax period onward by Notification 19/2021-CT (GSTR-3B) and 20/2021-CT (GSTR-1)",
      "Statutory (un-notified) rate remains ₹100/day per Act capped ₹5,000 per Act — the notified reduced rates/caps override it for 3B/GSTR-1",
      "Practical note: GSTR-3B late fee is auto-debited by the portal at next filing; GSTR-1 late fee is levied but historically not auto-collected at filing (recoverable by notice) — see unverified",
    ],
    capsAndFloors:
      "Caps (total CGST+SGST, per return): NIL return — ₹500. AATO ≤ ₹1.5 crore in preceding FY — ₹2,000. AATO > ₹1.5–5 crore — ₹5,000. AATO > ₹5 crore — ₹10,000 (the un-reduced statutory cap).",
    citationOld:
      "CGST Act 2017 s.47; reduced rates via Notifications 76/2018, then 19/2021 & 20/2021-Central Tax dated 01-Jun-2021",
    citationNew:
      "Current GST provision (unchanged by the Income-tax Act 2025): CGST s.47 + Notifications 19/2021 & 20/2021-CT",
    confidence: "confirmed",
    sourceUrl: "https://cleartax.in/s/gst-return-late-fees",
  },
  {
    key: "tds_201_1a",
    title: "Interest on late deduction / late deposit of TDS (s.201(1A))",
    trigger:
      "Dual, mutually exclusive periods: (a) tax deductible but deducted late — LATE DEDUCTION; (b) tax deducted but deposited after the due date (generally the 7th of the following month; 30 April for March deductions) — LATE DEPOSIT.",
    rate:
      "1% per month or part of a month for late DEDUCTION; 1.5% per month or part of a month for late DEPOSIT.",
    periodBasis:
      "Per month or part of a month, computed date-to-date: late deduction runs from the DATE the tax was deductible (payment/credit, whichever earlier) to the DATE of actual deduction; late deposit runs from the DATE OF DEDUCTION (not the due date) to the date of actual payment to the government. Any part of a month counts as a full month — because the 1.5% clock starts at the deduction date, a single day's delay past the due date commonly costs 2 months' interest (e.g. deducted 21 May, due 7 Jun, paid 8 Jun → 21 May–8 Jun spans 2 part-months = 3%).",
    formula:
      "Interest = TDS amount × 1% × months(deductible-date → deduction-date) + TDS amount × 1.5% × months(deduction-date → deposit-date); each 'months' rounds any fraction UP.",
    exceptions: [
      "Mandatory and automatic — no waiver discretion; must be paid before filing the TDS statement",
      "Not deductible as a business expense",
      "Payer treated as assessee-in-default relief (payee filed return, included income, paid tax + CA certificate in Form 26A) removes the s.201(1) default but interest u/s 201(1A) for late-deduction still runs to the payee's return-filing date",
    ],
    citationOld: "s.201(1A), Income-tax Act 1961",
    citationNew:
      "s.398, Income-tax Act 2025 (consolidates s.201(1)/(1A) and the TCS mirror s.206C(7); same 1% / 1.5% per month or part, effective tax year 2026-27)",
    confidence: "confirmed",
    sourceUrl: "https://www.aubsp.com/income-tax-act-2025-section-398/",
  },
  {
    key: "tds_234e",
    title: "Fee for late filing of TDS/TCS statements (s.234E)",
    trigger:
      "Quarterly TDS/TCS statement (24Q/26Q/27Q/27EQ) furnished after its due date (generally 31 Jul/31 Oct/31 Jan for Q1–Q3 and 31 May for Q4 of TDS statements).",
    rate:
      "₹200 per day for every day the default continues. This is a FEE, not interest or penalty — mandatory, no discretion to waive.",
    periodBasis:
      "Per calendar DAY, from the day after the statement due date to the date of actual furnishing (no month rounding).",
    formula:
      "Fee = min(₹200 × days late, total TDS/TCS deductible/collectible reported in that statement). Must be deposited BEFORE the statement can be filed. Computed per statement, per quarter.",
    exceptions: [
      "Hard cap: fee can never exceed the TDS/TCS amount in the statement — a nil-deduction statement attracts no 234E fee",
      "Separate and additional: penalty u/s 271H (₹10,000–₹1,00,000) for non-filing beyond one year or incorrect statements — a penalty, not calculator-computable; 2025 Act equivalent s.461",
      "Applies to statements due on/after 1-Jul-2012 only",
    ],
    capsAndFloors: "Capped at the amount of TDS/TCS in the statement.",
    citationOld: "s.234E, Income-tax Act 1961",
    citationNew:
      "s.427, Income-tax Act 2025 (₹200/day, same TDS/TCS-amount cap, effective tax year 2026-27); penalty counterpart s.461 (ex-271H)",
    confidence: "confirmed",
    sourceUrl:
      "https://blog.tdsman.com/2026/07/late-filing-of-tds-tcs-statements-fee-penalty-and-compliance-requirements/",
  },
];

export function getRule(key: RuleKey): InterestRule {
  const rule = RULES.find((r) => r.key === key);
  if (!rule) throw new Error(`Unknown rule key: ${key}`);
  return rule;
}

// ─────────────────────────────────────────────────────────────────────────────
// Date & rounding helpers (pure; ISO "YYYY-MM-DD" in, numbers out)
// ─────────────────────────────────────────────────────────────────────────────

type YMD = { y: number; m: number; d: number };

function parts(iso: string): YMD | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!m) return null;
  return { y: Number(m[1]), m: Number(m[2]), d: Number(m[3]) };
}

function utcMs(iso: string): number | null {
  const p = parts(iso);
  if (!p) return null;
  return Date.UTC(p.y, p.m - 1, p.d);
}

const DAY_MS = 86_400_000;

/** Rule 119A: round an interest base DOWN to the nearest multiple of ₹100. */
export function roundDown100(n: number): number {
  return Math.floor(Math.max(0, n) / 100) * 100;
}

/**
 * "Month or part of a month", anniversary (date-to-date) counting from
 * `startISO` INCLUSIVE: 16-Sep → 15-Dec = 3 months; 16-Sep → 16-Dec = 4
 * (three full months + one day = part → rounds UP, Rule 119A).
 * Returns 0 when end is before start. Used for 234A (start = day after due
 * date) and 234B (start = 1 April of the AY).
 */
export function monthsOrPartBetween(startISO: string, endISO: string): number {
  const s = parts(startISO);
  const e = parts(endISO);
  if (!s || !e) return 0;
  const sMs = Date.UTC(s.y, s.m - 1, s.d);
  const eMs = Date.UTC(e.y, e.m - 1, e.d);
  if (eMs < sMs) return 0;
  let months = (e.y - s.y) * 12 + (e.m - s.m);
  if (e.d >= s.d) months += 1; // the part month counts as a full month
  return Math.max(0, months);
}

/** 234A wrapper: period runs from the DAY AFTER the due date to the filing date. */
export function monthsOrPartFromDayAfter(dueISO: string, endISO: string): number {
  const due = utcMs(dueISO);
  const end = utcMs(endISO);
  if (due === null || end === null || end <= due) return 0;
  const dayAfter = new Date(due + DAY_MS);
  const iso = `${dayAfter.getUTCFullYear()}-${String(dayAfter.getUTCMonth() + 1).padStart(2, "0")}-${String(dayAfter.getUTCDate()).padStart(2, "0")}`;
  return monthsOrPartBetween(iso, endISO);
}

/**
 * s.201(1A) month counting: number of distinct CALENDAR months the interval
 * touches, both endpoints inclusive (21-May → 8-Jun touches May + June = 2).
 * This is the only basis that reproduces the research's own example, and it is
 * how TRACES computes 201(1A) interest in practice.
 */
export function calendarMonthsSpanned(fromISO: string, toISO: string): number {
  const s = parts(fromISO);
  const e = parts(toISO);
  if (!s || !e) return 0;
  if (Date.UTC(e.y, e.m - 1, e.d) < Date.UTC(s.y, s.m - 1, s.d)) return 0;
  return (e.y - s.y) * 12 + (e.m - s.m) + 1;
}

/**
 * Days from the DAY AFTER `dueISO` up to and including `untilISO`
 * (= simple date difference). 0 when filing/payment is on or before the due
 * date. Used for GST s.50(1) interest, GST late fee, and the 234E fee — all
 * of which run "from the day after the due date to the date of
 * payment/filing" per the research.
 */
export function daysAfterUntil(dueISO: string, untilISO: string): number {
  const due = utcMs(dueISO);
  const until = utcMs(untilISO);
  if (due === null || until === null) return 0;
  return Math.max(0, Math.round((until - due) / DAY_MS));
}

/**
 * TDS deposit due date for a given deduction date: "generally the 7th of the
 * following month; 30 April for March deductions" (research, s.201(1A)
 * trigger). Government deductors and quarterly-permission cases differ — the
 * UI lets the user override the date.
 */
export function tdsDepositDueDate(deductionISO: string): string | null {
  const p = parts(deductionISO);
  if (!p) return null;
  if (p.m === 3) return `${p.y}-04-30`;
  const ny = p.m === 12 ? p.y + 1 : p.y;
  const nm = p.m === 12 ? 1 : p.m + 1;
  return `${ny}-${String(nm).padStart(2, "0")}-07`;
}

// ─────────────────────────────────────────────────────────────────────────────
// Compute functions — each returns a step-by-step breakdown, never just a total
// ─────────────────────────────────────────────────────────────────────────────

export type BreakdownStep = {
  label: string;
  /** The formula WITH the numbers substituted in, e.g. "1% × 4 × ₹2,50,000". */
  formula: string;
  amount: number;
  /** How the UI should format `amount`. Defaults to rupees. */
  unit?: "inr" | "months" | "days";
};

export type ComputeResult = {
  steps: BreakdownStep[];
  total: number;
  /** True when the rule simply does not apply on these inputs. */
  nil: boolean;
  notes: string[];
};

const fmtINR = (n: number, dp = 0): string =>
  `₹${n.toLocaleString("en-IN", { minimumFractionDigits: dp, maximumFractionDigits: dp })}`;

// ── s.234A ───────────────────────────────────────────────────────────────────

export type Input234A = {
  taxOnTotalIncome: number;
  advanceTaxPaid: number;
  tdsTcs: number;
  /** Relief u/s 89/90/90A/91 (sum). */
  relief: number;
  matAmtCredit: number;
  /** Self-assessment tax paid on or before the due date (excluded from A). */
  selfAssessmentPaidByDue: number;
  dueDateISO: string;
  filingDateISO: string;
};

export function compute234A(i: Input234A): ComputeResult {
  const steps: BreakdownStep[] = [];
  const notes: string[] = [];
  const aRaw =
    i.taxOnTotalIncome -
    i.advanceTaxPaid -
    i.tdsTcs -
    i.relief -
    i.matAmtCredit -
    i.selfAssessmentPaidByDue;
  steps.push({
    label: "Net unpaid tax (A)",
    formula: `${fmtINR(i.taxOnTotalIncome)} − ${fmtINR(i.advanceTaxPaid)} advance tax − ${fmtINR(i.tdsTcs)} TDS/TCS − ${fmtINR(i.relief)} relief − ${fmtINR(i.matAmtCredit)} MAT/AMT credit − ${fmtINR(i.selfAssessmentPaidByDue)} self-assessment paid by due date`,
    amount: aRaw,
  });
  if (aRaw <= 0) {
    notes.push(
      "Net unpaid tax is nil or negative — no 234A interest even if the return is late (net-unpaid-tax basis; CIT v. Prannoy Roy, SC).",
    );
    return { steps, total: 0, nil: true, notes };
  }
  const a = roundDown100(aRaw);
  steps.push({
    label: "A rounded down to nearest ₹100 (Rule 119A)",
    formula: `floor(${fmtINR(aRaw)} ÷ 100) × 100`,
    amount: a,
  });
  const t = monthsOrPartFromDayAfter(i.dueDateISO, i.filingDateISO);
  steps.push({
    label: "Months of delay T (part of a month = full month)",
    formula: `day after ${i.dueDateISO} → ${i.filingDateISO}`,
    amount: t,
    unit: "months",
  });
  if (t === 0) {
    notes.push("Return filed on or before the due date — no period of default.");
    return { steps, total: 0, nil: true, notes };
  }
  const interest = 0.01 * t * a;
  steps.push({
    label: "Interest u/s 234A",
    formula: `1% × ${t} × ${fmtINR(a)}`,
    amount: interest,
  });
  return { steps, total: interest, nil: false, notes };
}

// ── s.234B ───────────────────────────────────────────────────────────────────

export type Input234B = {
  taxOnTotalIncome: number;
  tdsTcs: number;
  relief: number;
  matAmtCredit: number;
  advanceTaxPaid: number;
  /** 1 April of the assessment year. */
  ayStartISO: string;
  /** Date of determination u/s 143(1) / regular assessment / SA-tax payment. */
  endISO: string;
};

export function compute234B(i: Input234B): ComputeResult {
  const steps: BreakdownStep[] = [];
  const notes: string[] = [];
  const assessedTax =
    i.taxOnTotalIncome - i.tdsTcs - i.relief - i.matAmtCredit;
  steps.push({
    label: "Assessed tax",
    formula: `${fmtINR(i.taxOnTotalIncome)} − ${fmtINR(i.tdsTcs)} TDS/TCS − ${fmtINR(i.relief)} relief − ${fmtINR(i.matAmtCredit)} MAT/AMT credit`,
    amount: assessedTax,
  });
  if (assessedTax < 10_000) {
    notes.push(
      "Net tax liability is below ₹10,000 — no liability to pay advance tax, so 234B does not apply.",
    );
    return { steps, total: 0, nil: true, notes };
  }
  const ninety = 0.9 * assessedTax;
  steps.push({
    label: "90% of assessed tax (the trigger threshold)",
    formula: `90% × ${fmtINR(assessedTax)}`,
    amount: ninety,
  });
  if (i.advanceTaxPaid >= ninety) {
    notes.push(
      "Advance tax paid is at least 90% of assessed tax — no 234B interest at all.",
    );
    return { steps, total: 0, nil: true, notes };
  }
  const shortfall = assessedTax - i.advanceTaxPaid;
  steps.push({
    label: "Shortfall (assessed tax − advance tax paid)",
    formula: `${fmtINR(assessedTax)} − ${fmtINR(i.advanceTaxPaid)}`,
    amount: shortfall,
  });
  const base = roundDown100(shortfall);
  steps.push({
    label: "Base rounded down to nearest ₹100 (Rule 119A)",
    formula: `floor(${fmtINR(shortfall)} ÷ 100) × 100`,
    amount: base,
  });
  const t = monthsOrPartBetween(i.ayStartISO, i.endISO);
  steps.push({
    label: "Months from 1 April of the AY (part = full month)",
    formula: `${i.ayStartISO} → ${i.endISO}`,
    amount: t,
    unit: "months",
  });
  const interest = 0.01 * t * base;
  steps.push({
    label: "Interest u/s 234B",
    formula: `1% × ${t} × ${fmtINR(base)}`,
    amount: interest,
  });
  notes.push(
    "Where self-assessment tax is paid mid-way, interest on that portion stops at the payment date (s.234B(2)) — this simple mode charges the full period on the whole shortfall.",
  );
  return { steps, total: interest, nil: false, notes };
}

// ── s.234C ───────────────────────────────────────────────────────────────────

export type Input234C = {
  /** Tax due on returned income, already net of TDS/TCS, reliefs and MAT/AMT
   *  credit. (The research formula nets "advance tax + TDS paid up to that
   *  date" against the cumulative percentage; taking tax due net-of-TDS and
   *  comparing against advance tax alone is the same computation without
   *  double counting.) */
  taxDueOnReturnedIncome: number;
  /** CUMULATIVE advance tax paid on or before each instalment date. */
  paidByJun15: number;
  paidBySep15: number;
  paidByDec15: number;
  paidByMar15: number;
  /** 44AD/44ADA presumptive: single instalment — 100% by 15 March. */
  presumptive: boolean;
};

export type Quarter234C = {
  instalment: string;
  requiredPct: number;
  requiredAmt: number;
  paid: number;
  shortfallRaw: number;
  /** Rule 119A: shortfall rounded down to nearest ₹100 before the 1% multiply. */
  shortfallBase: number;
  months: number;
  /** null = no tolerance band for this instalment. */
  toleranceNote: string | null;
  toleranceMet: boolean;
  interest: number;
};

export type Result234C = ComputeResult & { quarters: Quarter234C[] };

export function compute234C(i: Input234C): Result234C {
  const notes: string[] = [];
  const steps: BreakdownStep[] = [];
  const tax = i.taxDueOnReturnedIncome;
  if (tax < 10_000) {
    notes.push(
      "Net tax liability after TDS is below ₹10,000 — no advance-tax obligation, no 234C.",
    );
    return { steps, total: 0, nil: true, notes, quarters: [] };
  }
  const defs: Array<{
    instalment: string;
    pct: number;
    tolerancePct: number | null;
    months: number;
    paid: number;
  }> = i.presumptive
    ? [
        {
          instalment: "15 March (single instalment, 44AD/44ADA)",
          pct: 100,
          tolerancePct: null,
          months: 1,
          paid: i.paidByMar15,
        },
      ]
    : [
        { instalment: "15 June", pct: 15, tolerancePct: 12, months: 3, paid: i.paidByJun15 },
        { instalment: "15 September", pct: 45, tolerancePct: 36, months: 3, paid: i.paidBySep15 },
        { instalment: "15 December", pct: 75, tolerancePct: null, months: 3, paid: i.paidByDec15 },
        { instalment: "15 March", pct: 100, tolerancePct: null, months: 1, paid: i.paidByMar15 },
      ];

  const quarters: Quarter234C[] = defs.map((q) => {
    const requiredAmt = (q.pct / 100) * tax;
    const shortfallRaw = Math.max(0, requiredAmt - q.paid);
    const toleranceAmt =
      q.tolerancePct === null ? null : (q.tolerancePct / 100) * tax;
    const toleranceMet =
      toleranceAmt !== null && q.paid >= toleranceAmt;
    const shortfallBase = roundDown100(shortfallRaw);
    const interest =
      toleranceMet || shortfallBase === 0 ? 0 : 0.01 * q.months * shortfallBase;
    return {
      instalment: q.instalment,
      requiredPct: q.pct,
      requiredAmt,
      paid: q.paid,
      shortfallRaw,
      shortfallBase,
      months: q.months,
      toleranceNote:
        q.tolerancePct === null
          ? null
          : `No interest if ≥ ${q.tolerancePct}% (${fmtINR((q.tolerancePct / 100) * tax)}) paid by this date`,
      toleranceMet,
      interest,
    };
  });

  for (const q of quarters) {
    steps.push({
      label: `${q.instalment} — cumulative ${q.requiredPct}% due`,
      formula: q.toleranceMet
        ? `paid ${fmtINR(q.paid)} ≥ tolerance — no interest`
        : q.shortfallBase === 0
          ? `paid ${fmtINR(q.paid)} ≥ ${fmtINR(q.requiredAmt)} required — no shortfall`
          : `1% × ${q.months} × ${fmtINR(q.shortfallBase)} (shortfall ${fmtINR(q.shortfallRaw)} rounded down to ₹100)`,
      amount: q.interest,
    });
  }
  const total = quarters.reduce((s, q) => s + q.interest, 0);
  if (i.presumptive) {
    notes.push(
      "44AD/44ADA presumptive assessees owe a single instalment — 100% by 15 March; nothing is due for June/September/December.",
    );
  }
  notes.push(
    "Shortfall attributable to capital gains, lottery/casual income, dividend (other than deemed dividend) or a brand-new business may be exempt under the first proviso if tax on it is paid in the remaining instalments — this calculator does not model that carve-out.",
  );
  return { steps, total, nil: total === 0, notes, quarters };
}

// ── GST s.50(1) — delayed payment of tax ─────────────────────────────────────

export type InputGst50_1 = {
  /** Tax paid by debiting the electronic CASH ledger. */
  cashLedgerTax: number;
  /** Tax settled by ITC set-off (attracts no interest under the net-cash rule). */
  itcSetOff: number;
  dueDateISO: string;
  paymentDateISO: string;
  /** Return filed after s.73/74/74A proceedings commenced → gross basis. */
  postProceedings: boolean;
};

export function computeGst50_1(i: InputGst50_1): ComputeResult {
  const steps: BreakdownStep[] = [];
  const notes: string[] = [];
  const gross = i.cashLedgerTax + i.itcSetOff;
  const base = i.postProceedings ? gross : i.cashLedgerTax;
  steps.push({
    label: i.postProceedings
      ? "Interest base — GROSS liability (net-cash benefit lost: return follows s.73/74/74A proceedings)"
      : "Interest base — cash-ledger portion only (net-cash rule, proviso to s.50(1))",
    formula: i.postProceedings
      ? `${fmtINR(i.cashLedgerTax, 2)} cash + ${fmtINR(i.itcSetOff, 2)} ITC set-off`
      : `${fmtINR(gross, 2)} gross − ${fmtINR(i.itcSetOff, 2)} ITC set-off (no interest on ITC portion)`,
    amount: base,
  });
  const days = daysAfterUntil(i.dueDateISO, i.paymentDateISO);
  steps.push({
    label: "Days of delay (day after due date → payment date)",
    formula: `${i.dueDateISO} → ${i.paymentDateISO}`,
    amount: days,
    unit: "days",
  });
  if (days === 0 || base <= 0) {
    notes.push(
      days === 0
        ? "Paid on or before the due date — no interest."
        : "No cash-ledger tax on these inputs — the ITC-set-off portion attracts no interest on a belated self-assessed return.",
    );
    return { steps, total: 0, nil: true, notes };
  }
  const interest = (base * 0.18 * days) / 365;
  steps.push({
    label: "Interest u/s 50(1) @ 18% p.a., day-wise (Rule 88B)",
    formula: `${fmtINR(base, 2)} × 18% × ${days} ÷ 365`,
    amount: interest,
  });
  return { steps, total: interest, nil: false, notes };
}

// ── GST s.50(3) — wrongly availed AND utilised ITC ───────────────────────────

export type InputGst50_3 = {
  wronglyAvailedItc: number;
  utilisationDateISO: string;
  reversalDateISO: string;
  /** Credit-ledger balance never dipped below the wrongly availed amount. */
  neverUtilised: boolean;
};

export function computeGst50_3(i: InputGst50_3): ComputeResult {
  const steps: BreakdownStep[] = [];
  const notes: string[] = [];
  if (i.neverUtilised) {
    notes.push(
      "Wrongly availed ITC that was never UTILISED attracts no s.50(3) interest (CBIC clarification with Notification 09/2022 — ITC is 'utilised' only when the electronic credit ledger balance falls below the wrongly availed amount).",
    );
    return { steps, total: 0, nil: true, notes };
  }
  // TODO(confirm): the research states the period runs "from the date of
  // utilisation of the wrongly availed ITC to the date of reversal or payment"
  // but does not say whether the utilisation day itself is counted. We use the
  // simple date difference (same convention as s.50(1)); confirm the exact
  // endpoint treatment against Rule 88B(3) before relying on a boundary day.
  const days = daysAfterUntil(i.utilisationDateISO, i.reversalDateISO);
  steps.push({
    label: "Days from utilisation to reversal/payment",
    formula: `${i.utilisationDateISO} → ${i.reversalDateISO}`,
    amount: days,
    unit: "days",
  });
  if (days === 0 || i.wronglyAvailedItc <= 0) {
    notes.push("Reversed on the utilisation date or nil amount — no interest on these inputs.");
    return { steps, total: 0, nil: true, notes };
  }
  const interest = (i.wronglyAvailedItc * 0.18 * days) / 365;
  steps.push({
    label:
      "Interest u/s 50(3) @ 18% p.a., day-wise (Rule 88B; 18% notified retro from 1-Jul-2017 by Notification 09/2022-CT)",
    formula: `${fmtINR(i.wronglyAvailedItc, 2)} × 18% × ${days} ÷ 365`,
    amount: interest,
  });
  notes.push(
    "24% survives only as the statutory ceiling in s.50(3) after the Finance Act 2022 substitution — post-FA-2022 there is no case where 24% is actually charged.",
  );
  return { steps, total: interest, nil: false, notes };
}

// ── GST late fee (CGST s.47 + mirrored SGST) ─────────────────────────────────

export type AatoBand = "upto1_5cr" | "1_5to5cr" | "over5cr";

export const AATO_BANDS: ReadonlyArray<{ key: AatoBand; label: string; cap: number }> = [
  { key: "upto1_5cr", label: "AATO ≤ ₹1.5 crore (preceding FY)", cap: 2_000 },
  { key: "1_5to5cr", label: "AATO > ₹1.5 – 5 crore", cap: 5_000 },
  { key: "over5cr", label: "AATO > ₹5 crore", cap: 10_000 },
];

export type InputGstLateFee = {
  returnType: "GSTR-3B" | "GSTR-1";
  nilReturn: boolean;
  aatoBand: AatoBand;
  dueDateISO: string;
  filingDateISO: string;
};

export type GstLateFeeResult = ComputeResult & {
  cgstHalf: number;
  sgstHalf: number;
};

export function computeGstLateFee(i: InputGstLateFee): GstLateFeeResult {
  const steps: BreakdownStep[] = [];
  const notes: string[] = [];
  const days = daysAfterUntil(i.dueDateISO, i.filingDateISO);
  steps.push({
    label: "Days late (day after due date → filing date)",
    formula: `${i.dueDateISO} → ${i.filingDateISO}`,
    amount: days,
    unit: "days",
  });
  if (days === 0) {
    notes.push("Filed on or before the due date — no late fee.");
    return { steps, total: 0, nil: true, notes, cgstHalf: 0, sgstHalf: 0 };
  }
  const perDay = i.nilReturn ? 20 : 50;
  const uncapped = perDay * days;
  steps.push({
    label: `Per-day fee (${i.nilReturn ? "NIL return: ₹10 CGST + ₹10 SGST" : "normal return: ₹25 CGST + ₹25 SGST"})`,
    formula: `₹${perDay}/day × ${days} days`,
    amount: uncapped,
  });
  const band = AATO_BANDS.find((b) => b.key === i.aatoBand) ?? AATO_BANDS[0];
  const cap = i.nilReturn ? 500 : band.cap;
  steps.push({
    label: i.nilReturn
      ? "Cap — NIL return (Notifications 19/2021 & 20/2021-CT)"
      : `Cap — ${band.label} (Notifications 19/2021 & 20/2021-CT)`,
    formula: `min(${fmtINR(uncapped)}, ${fmtINR(cap)})`,
    amount: cap,
  });
  const fee = Math.min(uncapped, cap);
  steps.push({
    label: `Late fee for ${i.returnType} (total CGST + SGST)`,
    formula: `min(₹${perDay} × ${days}, ${fmtINR(cap)})`,
    amount: fee,
  });
  notes.push(
    "Compute CGST and SGST halves separately — each is half the total shown. No late fee under the IGST Act.",
  );
  if (i.returnType === "GSTR-1") {
    notes.push(
      "GSTR-1 late fee is levied but historically not auto-collected at filing (recoverable by notice) — practitioner observation, not verified against an official CBIC document.",
    );
  }
  return {
    steps,
    total: fee,
    nil: false,
    notes,
    cgstHalf: fee / 2,
    sgstHalf: fee / 2,
  };
}

// ── s.201(1A) — late deduction / late deposit of TDS ─────────────────────────

export type InputTds201 = {
  tdsAmount: number;
  /** Date tax was deductible (payment/credit, whichever earlier). */
  deductibleDateISO: string;
  deductionDateISO: string;
  depositDateISO: string;
};

export function computeTds201(i: InputTds201): ComputeResult {
  const steps: BreakdownStep[] = [];
  const notes: string[] = [];

  // Leg 1 — late deduction @ 1% per month or part, deductible date → deduction date.
  const dedLate =
    utcMs(i.deductionDateISO) !== null &&
    utcMs(i.deductibleDateISO) !== null &&
    (utcMs(i.deductionDateISO) as number) > (utcMs(i.deductibleDateISO) as number);
  const m1 = dedLate
    ? calendarMonthsSpanned(i.deductibleDateISO, i.deductionDateISO)
    : 0;
  const interest1 = i.tdsAmount * 0.01 * m1;
  steps.push({
    label: "Late deduction — months (calendar months touched, part = full)",
    formula: dedLate
      ? `${i.deductibleDateISO} → ${i.deductionDateISO}`
      : "deducted on the deductible date — no delay",
    amount: m1,
    unit: "months",
  });
  steps.push({
    label: "Interest @ 1%/month on late deduction",
    formula: `${fmtINR(i.tdsAmount)} × 1% × ${m1}`,
    amount: interest1,
  });

  // Leg 2 — late deposit @ 1.5% per month or part, DEDUCTION date → deposit date,
  // but only when the deposit missed its due date.
  const due = tdsDepositDueDate(i.deductionDateISO);
  const depositLate =
    due !== null &&
    utcMs(i.depositDateISO) !== null &&
    (utcMs(i.depositDateISO) as number) > (utcMs(due) as number);
  const m2 = depositLate
    ? calendarMonthsSpanned(i.deductionDateISO, i.depositDateISO)
    : 0;
  const interest2 = i.tdsAmount * 0.015 * m2;
  steps.push({
    label: `Late deposit — deposit due ${due ?? "?"}; months run from the DEDUCTION date`,
    formula: depositLate
      ? `${i.deductionDateISO} → ${i.depositDateISO} (calendar months touched)`
      : "deposited by the due date — no interest on this leg",
    amount: m2,
    unit: "months",
  });
  steps.push({
    label: "Interest @ 1.5%/month on late deposit",
    formula: `${fmtINR(i.tdsAmount)} × 1.5% × ${m2}`,
    amount: interest2,
  });

  const total = interest1 + interest2;
  steps.push({
    label: "Total interest u/s 201(1A)",
    formula: `${fmtINR(interest1)} + ${fmtINR(interest2)}`,
    amount: total,
  });
  notes.push(
    "Because the 1.5% clock starts at the deduction date (not the due date), a single day's delay past the due date commonly costs 2 months' interest.",
  );
  notes.push(
    "Deposit due date shown assumes the general rule — 7th of the following month, 30 April for March deductions. Government deductors and quarterly-permission cases differ.",
  );
  return { steps, total, nil: total === 0, notes };
}

// ── s.234E — late filing fee for TDS/TCS statements ──────────────────────────

export type Input234E = {
  /** Total TDS/TCS deductible/collectible reported in the statement. */
  tdsInStatement: number;
  dueDateISO: string;
  filingDateISO: string;
};

export function compute234E(i: Input234E): ComputeResult {
  const steps: BreakdownStep[] = [];
  const notes: string[] = [];
  const days = daysAfterUntil(i.dueDateISO, i.filingDateISO);
  steps.push({
    label: "Days late (day after due date → filing date)",
    formula: `${i.dueDateISO} → ${i.filingDateISO}`,
    amount: days,
    unit: "days",
  });
  if (days === 0) {
    notes.push("Statement furnished on or before the due date — no fee.");
    return { steps, total: 0, nil: true, notes };
  }
  const uncapped = 200 * days;
  steps.push({
    label: "Fee @ ₹200/day",
    formula: `₹200 × ${days} days`,
    amount: uncapped,
  });
  const fee = Math.min(uncapped, Math.max(0, i.tdsInStatement));
  steps.push({
    label: "Capped at the TDS/TCS amount in the statement",
    formula: `min(${fmtINR(uncapped)}, ${fmtINR(Math.max(0, i.tdsInStatement))})`,
    amount: fee,
  });
  if (i.tdsInStatement <= 0) {
    notes.push("A nil-deduction statement attracts no 234E fee (the cap is the TDS/TCS amount).");
  }
  notes.push(
    "This is a FEE, not interest or penalty — mandatory, no discretion to waive, and it must be deposited before the statement can be filed. Penalty u/s 271H (₹10,000–₹1,00,000) for defaults beyond one year is separate and not computed here.",
  );
  return { steps, total: fee, nil: fee === 0, notes };
}
